import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Paper,
  Fade,
  Zoom,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Work as WorkIcon,
  Business as BusinessIcon,
  School as SchoolIcon,
  Person as PersonIcon,
  ArrowForward as ArrowForwardIcon,
  Security as SecurityIcon,
  VerifiedUser as VerifiedUserIcon,
} from '@mui/icons-material';

const SignupSelection: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const signupOptions = [
    {
      id: 'techie',
      title: 'Tech Professional',
      icon: <WorkIcon sx={{ fontSize: 48 }} />,
      description: 'Join as a verified tech professional',
      features: [
        'Document verification',
        'Work authorization check',
        'Experience verification',
        'Education verification',
        'Fraud prevention'
      ],
      route: '/techie-signup',
      color: '#2196F3'
    },
    {
      id: 'hiring-manager',
      title: 'Hiring Manager',
      icon: <PersonIcon sx={{ fontSize: 48 }} />,
      description: 'Register as a hiring manager',
      features: [
        'Company verification',
        'Manager credentials',
        'Hiring authority',
        'Access to talent pool',
        'Verified hiring process'
      ],
      route: '/hiring-manager-signup',
      color: '#4CAF50'
    },
    {
      id: 'company',
      title: 'Company',
      icon: <BusinessIcon sx={{ fontSize: 48 }} />,
      description: 'Register your company',
      features: [
        'Company verification',
        'EIN/CIN validation',
        'Admin verification',
        'Job posting access',
        'Talent search'
      ],
      route: '/company-signup',
      color: '#FF9800'
    },
    {
      id: 'school',
      title: 'Educational Institution',
      icon: <SchoolIcon sx={{ fontSize: 48 }} />,
      description: 'Register your school/institution',
      features: [
        'Institution verification',
        'Admin verification',
        'Student access',
        'Course management',
        'Educational partnerships'
      ],
      route: '/school-signup',
      color: '#9C27B0'
    }
  ];

  const handleOptionSelect = (route: string) => {
    navigate(route);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      py: 4
    }}>
      <Container maxWidth="lg">
        <Fade in timeout={800}>
          <Paper 
            elevation={24} 
            sx={{ 
              p: 4, 
              mb: 4, 
              borderRadius: 3,
              background: 'rgba(255, 255, 255, 0.95)',
              backdropFilter: 'blur(10px)'
            }}
          >
            <Box textAlign="center" mb={4}>
              <Typography 
                variant="h3" 
                component="h1" 
                gutterBottom 
                sx={{ 
                  fontWeight: 800,
                  background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  mb: 2
                }}
              >
                Join VerTechie
              </Typography>
              <Typography 
                variant="h6" 
                color="text.secondary" 
                paragraph
                sx={{ maxWidth: 600, mx: 'auto' }}
              >
                Choose your role to begin the secure registration process. All registrations include comprehensive verification and fraud prevention.
              </Typography>
              
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 2, mt: 3 }}>
                <SecurityIcon color="primary" />
                <Typography variant="body2" color="text.secondary">
                  Enterprise-grade security and verification
                </Typography>
                <VerifiedUserIcon color="success" />
              </Box>
            </Box>

            <Grid container spacing={3} justifyContent="center">
              {signupOptions.map((option, index) => (
                <Grid item xs={12} sm={6} md={3} key={option.id}>
                  <Zoom in timeout={600 + index * 200}>
                    <Card
                      sx={{
                        height: '100%',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease-in-out',
                        border: '1px solid',
                        borderColor: 'divider',
                        '&:hover': {
                          transform: 'scale(1.02)',
                          boxShadow: `0 8px 32px ${option.color}30`,
                          borderColor: option.color,
                        }
                      }}
                      onClick={() => handleOptionSelect(option.route)}
                    >
                      <CardActionArea sx={{ height: '100%', p: 3 }}>
                        <CardContent sx={{ textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column' }}>
                          <Box sx={{ color: option.color, mb: 2 }}>
                            {option.icon}
                          </Box>
                          
                          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 700 }}>
                            {option.title}
                          </Typography>
                          
                          <Typography variant="body1" color="text.secondary" paragraph>
                            {option.description}
                          </Typography>

                          <Box sx={{ mt: 'auto' }}>
                            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                              Includes:
                            </Typography>
                            {option.features.map((feature, idx) => (
                              <Box 
                                key={idx} 
                                sx={{ 
                                  display: 'flex', 
                                  alignItems: 'center', 
                                  mb: 1,
                                  justifyContent: 'center'
                                }}
                              >
                                <Box
                                  sx={{
                                    width: 6,
                                    height: 6,
                                    borderRadius: '50%',
                                    bgcolor: option.color,
                                    mr: 1
                                  }}
                                />
                                <Typography variant="body2" color="text.secondary">
                                  {feature}
                                </Typography>
                              </Box>
                            ))}
                          </Box>

                          <Box sx={{ mt: 2 }}>
                            <ArrowForwardIcon sx={{ color: option.color }} />
                          </Box>
                        </CardContent>
                      </CardActionArea>
                    </Card>
                  </Zoom>
                </Grid>
              ))}
            </Grid>

            <Box sx={{ textAlign: 'center', mt: 4 }}>
              <Typography variant="body2" color="text.secondary">
                <strong>Important:</strong> All information provided will be verified through official documents and cross-referenced with our verification partners. Any false information will result in permanent platform ban.
              </Typography>
            </Box>
          </Paper>
        </Fade>
      </Container>
    </Box>
  );
};

export default SignupSelection;

